import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Drawer,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format, isBefore, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { TimeSlot } from '../../../../domain/myShift';
import { DaySlotSelection } from '../types';
import { TimeSlotPicker } from './TimeSlotPicker';

/**
 * シフト提出フォームのProps
 */
interface ShiftSubmissionFormProps {
  /** フォーム表示フラグ */
  open: boolean;
  /** 選択中の日付 */
  selectedDate?: Date;
  /** 月次シフト枠フェーズ */
  phase: string | null;
  /** スロット一覧 */
  slots: DaySlotSelection[];
  /** 募集枠に一致しない自分のシフト（読み取り専用表示） */
  extraShifts: TimeSlot[];
  /** このフェーズで編集可能か */
  editable: boolean;
  /** スロット選択変更ハンドラー */
  onSlotChange: (key: string, selected: boolean) => void;
  /** 確定ボタンハンドラー（確認ダイアログを開く） */
  onSubmit: () => void;
  /** キャンセルハンドラー */
  onCancel: () => void;
  /** 送信中フラグ */
  isSubmitting: boolean;
}

/** フェーズ別のフォームタイトル */
const getTitle = (phase: string | null, editable: boolean): string => {
  if (phase === 'fixed_recruiting') return '勤務不可の選択';
  if (editable) return '希望の選択';
  return 'シフトの確認';
};

/** フェーズ別の注意書き（編集不可フェーズ） */
const getPhaseNotice = (phase: string | null): string | null => {
  switch (phase) {
    case 'fixed_closed':
      return '固定シフト募集は締め切られました。この日のシフトは変更できません。';
    case 'fixed_confirmed':
      return '固定シフトは確定済みです。通常募集の開始までお待ちください。';
    case 'closed':
      return '募集は締め切られました。希望の選択状態は変更できません。';
    case 'confirmed':
      return 'シフトは確定済みです。';
    case 'recruiting':
    case 'fixed_recruiting':
      return null;
    default:
      return '募集開始前です。募集が始まると希望を提出できます。';
  }
};

/**
 * シフト提出フォームコンポーネント
 * @description フェーズに応じて「希望」「不可」の選択、または読み取り専用表示を行う
 */
export const ShiftSubmissionForm: React.FC<ShiftSubmissionFormProps> = ({
  open,
  selectedDate,
  phase,
  slots,
  extraShifts,
  editable,
  onSlotChange,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const today = startOfDay(new Date());
  const isPastDate = selectedDate ? isBefore(selectedDate, today) : false;
  const phaseNotice = getPhaseNotice(phase);
  const canSubmit = editable && !isPastDate && !isSubmitting && slots.some(s => !s.locked);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 480 }, borderLeft: '1px solid #dedbd7' },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* ヘッダー */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6">{getTitle(phase, editable)}</Typography>
          <IconButton
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="閉じる"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 選択日表示 */}
        {selectedDate && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              選択日
            </Typography>
            <Typography variant="h6">
              {format(selectedDate, 'yyyy年M月d日（E）', { locale: ja })}
            </Typography>
            {isPastDate && editable && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                過去の日付は編集できません
              </Alert>
            )}
          </Paper>
        )}

        {/* フェーズ注意書き */}
        {phaseNotice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {phaseNotice}
          </Alert>
        )}

        {/* スロット選択 */}
        <TimeSlotPicker
          slots={slots}
          onChange={onSlotChange}
          disabled={!editable || isPastDate}
          variant={phase === 'fixed_recruiting' ? 'unavailable' : 'preferred'}
        />

        {/* その他の予定（募集枠に一致しない自分のシフト） */}
        {extraShifts.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              その他の予定（この画面では変更できません）
            </Typography>
            {extraShifts.map(slot => (
              <Typography key={`${slot.startTime}-${slot.endTime}`} variant="body2" sx={{ py: 0.5 }}>
                {slot.startTime} - {slot.endTime}
              </Typography>
            ))}
          </Box>
        )}

        {/* アクションボタン */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onCancel}
            disabled={isSubmitting}
          >
            キャンセル
          </Button>
          {editable && (
            <Button
              variant="contained"
              fullWidth
              onClick={onSubmit}
              disabled={!canSubmit}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              確定
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ShiftSubmissionForm;
